import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent, ModalData, ModalRef, numberToHashRate } from '@ui-lib/components';
import { ScannerService } from '../../scanner.service';
import { ScannerSignalRService } from '../../scanner-signalr.service';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ISetModelModalData, SetScanResultModelForm } from './set-model-modal.model';
import { take } from 'rxjs';
import { EquipmentModelSelectComponent } from '../../../../shared/components/equipment-model-select/equipment-model-select.component';

@Component({
  selector: 'mon-set-model',
  templateUrl: './set-model-modal.component.html',
  styleUrl: './set-model-modal.component.scss',
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    ReactiveFormsModule,
    ButtonComponent,
    EquipmentModelSelectComponent,
  ],
  providers: [ScannerService, ScannerSignalRService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SetModelModalComponent extends ModalData<ISetModelModalData> {
  private readonly scannerService = inject(ScannerService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly modalRef = inject(ModalRef);

  /**
   * Найденный на устройстве эталон, приведённый к читаемому виду.
   *
   * Модель здесь ещё не выбрана, поэтому `nominalHashrateInModelUnits` всегда `null` и
   * пересчитать значение в шкалу модели некому. Показываем базовое значение, отформатированное
   * тем же помощником, что и остальные хешрейты — сырое число в H/s (188000000000000)
   * оператору бесполезно.
   *
   * Для Sol-семейства число не показываем: базовая единица эталона (Sol/s) не совпадает
   * с той, что ожидает форматтер (KSol/s), и ошибиться на три порядка хуже, чем промолчать.
   */
  protected get referenceValue(): string | null {
    const reference = this.modalData.referenceSpec;
    const nominalHashrate = reference?.nominalHashrate ?? null;

    if (nominalHashrate === null || nominalHashrate <= 0) {
      return null;
    }

    if (this.isSolutionsUnit(reference?.rawUnit ?? null)) {
      return null;
    }

    const { hashRate, literal } = numberToHashRate(nominalHashrate);

    return `${hashRate} ${literal}`;
  }

  private isSolutionsUnit(rawUnit: string | null): boolean {
    return (rawUnit ?? '').toLowerCase().includes('sol');
  }

  protected readonly setModelForm = this.formBuilder.group<SetScanResultModelForm>({
    equipmentModelId: this.formBuilder.control(null, Validators.required),
  });

  public onSelectModel(): void {
    if (this.setModelForm.invalid) return;

    const value = this.setModelForm.getRawValue();
    if (!value.equipmentModelId) return;

    this.scannerService
      .setScanResultModel(this.modalData.scanResultId, value.equipmentModelId.id)
      .pipe(take(1))
      .subscribe(() => this.modalRef.close(true));
  }
}
